import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, PackageCheck, ThumbsUp, ThumbsDown, Trash2 } from 'lucide-react';

export function ReviewPanel({ internalNote, onNoteChange, onReview, submitting, canCancel, onCancel }) {
  const { t } = useTranslation();
  return (
    <Card className="bg-white border-gray-200/80 shadow-none">
      <CardContent className="pt-5 space-y-4">
        <div>
          <Label className="text-sm text-gray-600">{t('internal_note')}</Label>
          <Textarea value={internalNote} onChange={e => onNoteChange(e.target.value)} placeholder={t('internal_notes')} className="bg-gray-50 border-gray-200 text-sm mt-1" />
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="send-decision-btn" onClick={onReview} disabled={submitting}
            className="bg-emerald-700 hover:bg-emerald-800 flex-1 text-sm h-9">
            {submitting ? t('submitting') : (t('confirm_decision') || 'Confirm Decision')}
          </Button>
          {canCancel && (
            <Button data-testid="cancel-request-btn" variant="outline" onClick={onCancel}
              className="text-sm h-9 flex-1 border-gray-300 text-gray-500 hover:text-red-500 hover:border-red-300">{t('cancel_request')}</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CancellationPanel({ onApproveCancellation, onRejectCancellation }) {
  const { t } = useTranslation();
  return (
    <Card className="bg-amber-50/50 border-amber-200/80 shadow-none">
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-center gap-2">
          <AlertCircle size={16} className="text-amber-600" />
          <p className="text-sm font-medium text-amber-700">{t('cancellation_pending_msg')}</p>
        </div>
        <div className="flex gap-3">
          <Button data-testid="approve-cancellation-btn" onClick={onApproveCancellation}
            className="bg-red-600 hover:bg-red-700 text-white text-sm h-9 flex-1">{t('approve_cancellation')}</Button>
          <Button data-testid="reject-cancellation-btn" variant="outline" onClick={onRejectCancellation}
            className="text-sm h-9 flex-1 border-gray-300">{t('reject_cancellation')}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function CancellationPendingBadge() {
  const { t } = useTranslation();
  return (
    <Card className="bg-amber-50/50 border-amber-200/80 shadow-none">
      <CardContent className="pt-5">
        <div className="flex items-center gap-2">
          <AlertCircle size={16} className="text-amber-600" />
          <p className="text-sm text-amber-700">{t('cancellation_awaiting_approval')}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function DeliveryConfirmedBadge({ request }) {
  const { t } = useTranslation();
  return (
    <Card className="bg-green-50/50 border-green-200/80 shadow-none">
      <CardContent className="pt-5">
        <div className="flex items-center gap-2">
          <PackageCheck size={16} className="text-green-600" />
          <p className="text-sm font-medium text-green-700">{t('delivered_confirmed_label')}</p>
          {request.delivery_confirmed_at && <span className="text-xs text-green-500 ml-auto">{request.delivery_confirmed_at?.split('T')[0]}</span>}
        </div>
        {request.delivery_note && <p className="text-xs text-green-600/70 mt-1 ml-6">{request.delivery_note}</p>}
      </CardContent>
    </Card>
  );
}

export function DeliveryPanel({ deliveryNote, onDeliveryNoteChange, onConfirmDelivery, confirming }) {
  const { t } = useTranslation();
  return (
    <Card className="bg-white border-gray-200/80 shadow-none">
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-center gap-2">
          <PackageCheck size={16} className="text-gray-600" />
          <p className="text-sm font-medium text-gray-700">{t('confirm_delivery')}</p>
        </div>
        <Textarea data-testid="delivery-note-input" value={deliveryNote} onChange={e => onDeliveryNoteChange(e.target.value)}
          placeholder={t('delivery_note_placeholder')} className="bg-gray-50 border-gray-200 text-sm" rows={2} />
        <Button data-testid="confirm-delivery-btn" onClick={onConfirmDelivery} disabled={confirming}
          className="bg-green-600 hover:bg-green-700 text-white text-sm h-9">
          <PackageCheck size={14} className="mr-1.5" /> {confirming ? '...' : t('mark_as_delivered')}
        </Button>
      </CardContent>
    </Card>
  );
}

export function SponsorApprovalPanel({ internalNote, onNoteChange, onApprove }) {
  const { t } = useTranslation();
  return (
    <Card className="bg-purple-50/50 border-purple-200/80 shadow-none">
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-center gap-2">
          <AlertCircle size={16} className="text-purple-600" />
          <p className="text-sm font-medium text-purple-700">{t('pending_your_approval')}</p>
        </div>
        <Textarea data-testid="sponsor-approval-note" value={internalNote} onChange={e => onNoteChange(e.target.value)}
          placeholder={t('add_notes_before_approval')} className="bg-white border-gray-200 text-sm" rows={2} />
        <div className="flex gap-3">
          <Button data-testid="sponsor-approve-btn" onClick={() => onApprove(true)}
            className="bg-green-600 hover:bg-green-700 text-white text-sm h-9 flex-1">
            <ThumbsUp size={14} className="mr-1.5" /> {t('approve_and_submit')}
          </Button>
          <Button data-testid="sponsor-reject-btn" variant="outline" onClick={() => onApprove(false)}
            className="text-sm h-9 flex-1 border-red-300 text-red-600 hover:bg-red-50">
            <ThumbsDown size={14} className="mr-1.5" /> {t('reject_request')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function DeleteRequestButton({ onDelete }) {
  return (
    <div className="flex justify-end">
      <Button data-testid="delete-request-btn" variant="ghost" onClick={onDelete}
        className="text-xs h-7 text-gray-400 hover:text-red-500">
        <Trash2 size={12} className="mr-1" /> Delete Permanently
      </Button>
    </div>
  );
}
